/**
 * "Save progress": the player's QR code, plus buttons to download it as a
 * save picture or copy it as a link. Opened from the Results screen.
 *
 * Keyboard: Up/Down to choose, Enter to press, Esc to close.
 */
import Phaser from 'phaser';
import { encodeProfileCode } from '../../systems/profileCode';
import type { ProfileSlot } from '../../systems/SaveManager';
import {
  downloadBlob,
  peekingQrCanvas,
  renderSaveCard,
  saveLink,
} from '../../systems/saveTransfer';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../../types';
import { pressButton, styleButton } from './menuButton';

const DEPTH = 300;
const PANEL_W = 1240;
const PANEL_H = 700;
const QR_BOX = 600;
/** A lighter code for the screen; see the constructor. */
const SCREEN_CODE_LIMIT = 1600;

export class SaveProgressPanel {
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly buttons: Phaser.GameObjects.Text[] = [];
  private readonly actions: Array<() => void> = [];
  private readonly status: Phaser.GameObjects.Text;
  private readonly link: string;
  private readonly pictureLink: string;
  private selected = 0;
  private textureKey = '';
  private readonly onKey: (event: KeyboardEvent) => void;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly profile: ProfileSlot,
    private readonly onClose: () => void,
  ) {
    // Two codes. The on-screen one is kept lighter so a phone can read it off
    // a screen: a player with a long history gives some of it up there, but
    // never any progress. The downloaded picture is crisp, so it carries more.
    this.link = saveLink(encodeProfileCode(profile, SCREEN_CODE_LIMIT).code);
    this.pictureLink = saveLink(encodeProfileCode(profile).code);

    // Wide stage: QR on the left, words and buttons on the right. Tall stage:
    // one column - QR on top, words and full-width buttons beneath.
    const panelW = IS_PORTRAIT ? GAME_WIDTH - 32 : PANEL_W;
    const panelH = IS_PORTRAIT ? GAME_HEIGHT - 80 : PANEL_H;
    const qrBox = IS_PORTRAIT ? panelW - 100 : QR_BOX;
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const left = cx - panelW / 2;
    const top = cy - panelH / 2;

    // A dim layer that also swallows clicks meant for the screen behind.
    const shade = scene.add
      .rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x05080f, 0.78)
      .setInteractive();
    const panel = scene.add.graphics();
    panel.fillStyle(0x16233a, 1);
    panel.fillRoundedRect(left, top, panelW, panelH, 28);
    panel.lineStyle(4, 0x8ecae6, 1);
    panel.strokeRoundedRect(left, top, panelW, panelH, 28);

    // The styled QR with the penguin peeking over it, drawn once to a canvas
    // at the size it is shown, so it stays sharp.
    const qx = left + 30;
    // Drawn at twice the size and shown at half, so it is sharp on big screens.
    const canvas = peekingQrCanvas(this.link, qrBox * 2);
    const key = `save-qr-${Date.now()}`;
    scene.textures.addCanvas(key, canvas);
    this.textureKey = key;
    const qr = IS_PORTRAIT
      ? scene.add
          .image(cx, top + 28, key)
          .setOrigin(0.5, 0)
          .setScale(0.5)
      : scene.add
          .image(qx, top + panelH / 2, key)
          .setOrigin(0, 0.5)
          .setScale(0.5);

    const textX = IS_PORTRAIT ? left + 44 : qx + qrBox + 44;
    const textW = IS_PORTRAIT ? panelW - 88 : left + panelW - textX - 40;
    // Where the words start: beside the QR, or under it.
    const textTop = IS_PORTRAIT ? top + 28 + canvas.height / 2 + 24 : top + 56;
    const heading = scene.add.text(textX, textTop, 'Save your progress', {
      fontFamily: FONT_FAMILY,
      fontSize: '40px',
      color: PALETTE.snow,
    });
    const body = scene.add.text(
      textX,
      textTop + 64,
      `Scan this with a phone or tablet camera to keep playing as ${profile.name} there.\n\nOr keep a save picture: load it any time in Settings.`,
      {
        fontFamily: FONT_FAMILY,
        fontSize: '22px',
        color: PALETTE.ice,
        wordWrap: { width: textW },
        lineSpacing: 6,
      },
    );

    const items: ReadonlyArray<readonly [string, () => void]> = [
      ['Download save picture', () => void this.download()],
      ['Copy save link', () => void this.copy()],
      ['Close', () => this.close()],
    ];
    items.forEach(([label, action], index) => {
      const button = scene.add
        .text(
          IS_PORTRAIT ? cx : textX,
          IS_PORTRAIT ? top + panelH - 290 + index * 84 : top + 380 + index * 72,
          label,
          {
            fontFamily: FONT_FAMILY,
            fontSize: '28px',
            color: PALETTE.snow,
            backgroundColor: '#1b2a41',
            padding: { x: 18, y: 10 },
          },
        )
        .setInteractive({ useHandCursor: true });
      if (IS_PORTRAIT) button.setOrigin(0.5, 0).setFixedSize(textW, 0).setAlign('center');
      button.on('pointerover', () => this.select(index));
      button.on('pointerup', () => pressButton(button, action));
      this.buttons.push(button);
      this.actions.push(action);
    });

    this.status = scene.add.text(textX, IS_PORTRAIT ? top + panelH - 340 : top + panelH - 52, '', {
      fontFamily: FONT_FAMILY,
      fontSize: '22px',
      color: PALETTE.teal,
      wordWrap: { width: textW },
    });

    this.objects.push(shade, panel, qr, heading, body, ...this.buttons, this.status);
    for (const object of this.objects) {
      (object as unknown as Phaser.GameObjects.Components.Depth).setDepth(DEPTH);
      (object as unknown as Phaser.GameObjects.Components.ScrollFactor).setScrollFactor(0);
    }
    this.select(0);

    this.onKey = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowUp') this.select((this.selected + 2) % 3);
      else if (event.key === 'ArrowDown') this.select((this.selected + 1) % 3);
      else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const button = this.buttons[this.selected];
        const action = this.actions[this.selected];
        if (button !== undefined && action !== undefined) pressButton(button, action);
      } else if (event.key === 'Escape') this.close();
    };
    scene.input.keyboard?.on('keydown', this.onKey);
  }

  private select(index: number): void {
    this.selected = index;
    this.buttons.forEach((button, i) => styleButton(button, i === index));
  }

  private async download(): Promise<void> {
    try {
      const blob = await renderSaveCard({
        name: this.profile.name,
        stars: this.profile.totals.stars,
        levels: this.profile.totals.levelsCompleted,
        link: this.pictureLink,
      });
      const safeName = this.profile.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'player';
      downloadBlob(blob, `quest-jump-${safeName}.png`);
      this.setStatus('Saved to your Downloads folder!');
    } catch {
      this.setStatus('Could not make the picture. Try Copy save link instead.', PALETTE.sunset);
    }
  }

  private async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.link);
      this.setStatus('Copied! Paste it into a message or note to keep it safe.');
    } catch {
      window.prompt('Copy this save link:', this.link);
    }
  }

  private setStatus(text: string, color: string = PALETTE.teal): void {
    if (this.status.active) this.status.setText(text).setColor(color);
  }

  close(): void {
    this.scene.input.keyboard?.off('keydown', this.onKey);
    for (const object of this.objects) object.destroy();
    if (this.scene.textures.exists(this.textureKey)) this.scene.textures.remove(this.textureKey);
    this.onClose();
  }
}
